/**
 * Flattens a Route into one continuous polyline plus a step lookup table, so
 * that a GPS fix can be turned into "which step am I on and how far to the
 * next turn" with a couple of array lookups.
 *
 * We rebuild the line from the STEP geometries rather than using
 * `route.geometry`. They describe the same path, but only the step version
 * gives us exact vertex indices for step boundaries - and being off by even one
 * vertex at a junction makes the maneuver fire late.
 */

import type { LngLat, Route, RouteStep } from '../core/types';
import { cumulativeDistances, distance, nearestPointOnSegment } from '../core/geo';
import type { SnapResult } from '../core/types';

export interface IndexedStep {
  legIndex: number;
  stepIndex: number;
  step: RouteStep;
  /** Vertex index in the combined line where this step starts / ends. */
  startVertex: number;
  endVertex: number;
  /** Distance along the whole route to the start / end of this step, metres. */
  startDistance: number;
  endDistance: number;
}

export class RouteIndex {
  readonly coords: LngLat[] = [];
  readonly cumulative: number[];
  readonly steps: IndexedStep[] = [];
  readonly totalDistance: number;

  constructor(readonly route: Route) {
    for (let legIndex = 0; legIndex < route.legs.length; legIndex++) {
      const leg = route.legs[legIndex];
      for (let stepIndex = 0; stepIndex < leg.steps.length; stepIndex++) {
        const step = leg.steps[stepIndex];
        const line = step.geometry?.coordinates ?? [];
        const startVertex = Math.max(0, this.coords.length - (this.coords.length ? 1 : 0));

        // Steps share a vertex with their predecessor; drop the duplicate.
        const slice = this.coords.length > 0 ? line.slice(1) : line;
        for (const c of slice) this.coords.push(c);

        this.steps.push({
          legIndex,
          stepIndex,
          step,
          startVertex,
          endVertex: Math.max(startVertex, this.coords.length - 1),
          startDistance: 0,
          endDistance: 0,
        });
      }
    }

    if (this.coords.length === 0) this.coords.push([0, 0]);
    this.cumulative = cumulativeDistances(this.coords);
    this.totalDistance = this.cumulative[this.cumulative.length - 1];

    for (const s of this.steps) {
      s.startDistance = this.cumulative[s.startVertex] ?? 0;
      s.endDistance = this.cumulative[s.endVertex] ?? this.totalDistance;
    }
  }

  /**
   * Snap a position onto the route.
   *
   * `hintVertex` limits the search to a window around the last known position,
   * which is both faster and — more importantly — correct on routes that cross
   * themselves or run back alongside an earlier section. A global nearest-point
   * search would happily teleport you onto the opposite carriageway.
   */
  snap(p: LngLat, hintVertex = 0, windowVertices = 400): SnapResult {
    const n = this.coords.length;
    let from = 0;
    let to = n - 1;
    if (hintVertex > 0) {
      from = Math.max(0, hintVertex - 40);
      to = Math.min(n - 1, hintVertex + windowVertices);
    }

    let best = { dist: Infinity, index: from, t: 0, point: this.coords[from] };
    for (let i = from; i < to; i++) {
      const r = nearestPointOnSegment(p, this.coords[i], this.coords[i + 1]);
      if (r.distance < best.dist) {
        best = { dist: r.distance, index: i, t: r.t, point: r.point };
      }
    }

    // The windowed search failed to find anything plausible - the driver has
    // probably jumped (tunnel exit, app resumed). Fall back to a full scan.
    if (best.dist > 120 && (from > 0 || to < n - 1)) {
      for (let i = 0; i < n - 1; i++) {
        const r = nearestPointOnSegment(p, this.coords[i], this.coords[i + 1]);
        if (r.distance < best.dist) {
          best = { dist: r.distance, index: i, t: r.t, point: r.point };
        }
      }
    }

    const segLength = distance(this.coords[best.index], this.coords[best.index + 1] ?? this.coords[best.index]);
    const along = (this.cumulative[best.index] ?? 0) + segLength * best.t;

    const a = this.coords[best.index];
    const b = this.coords[best.index + 1] ?? a;
    const course =
      a === b
        ? 0
        : (Math.atan2(
            Math.sin(((b[0] - a[0]) * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180),
            Math.cos((a[1] * Math.PI) / 180) * Math.sin((b[1] * Math.PI) / 180) -
              Math.sin((a[1] * Math.PI) / 180) *
                Math.cos((b[1] * Math.PI) / 180) *
                Math.cos(((b[0] - a[0]) * Math.PI) / 180),
          ) *
            180) /
            Math.PI;

    return {
      location: best.point,
      deviation: best.dist,
      segmentIndex: best.index,
      distanceAlongRoute: along,
      courseOnRoute: (course + 360) % 360,
    };
  }

  /** The step containing a given distance along the route. */
  stepAt(distanceAlong: number, fromIndex = 0): number {
    for (let i = Math.max(0, fromIndex); i < this.steps.length; i++) {
      if (distanceAlong < this.steps[i].endDistance) return i;
    }
    return this.steps.length - 1;
  }
}
